import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit2, Trash2, X, Users, CheckCircle, RefreshCw, Package } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'Credit Card', 'Cheque', 'Online'];
const CURRENCIES    = ['USD', 'EUR', 'GBP', 'AED', 'INR', 'SAR', 'QAR', 'KWD'];

const PRESETS = [
  [5,1],[5,3],[5,6],[5,12],
  [10,1],[10,3],[10,6],[10,12],
  [25,6],[25,12],[50,12],
];

const emptyForm = {
  subscriptionid: null, max_users: '', duration_months: '',
  amount_paid: '', currency: 'USD', payment_reference: '',
  payment_mode: 'Cash', notes: '', isactive: 1,
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Subscriptions = () => {
  const [subs,      setSubs]      = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [errors,    setErrors]    = useState({});
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Subscriptions.php');
      if (res.data.success) { setSubs(res.data.data || []); setSummary(res.data.summary || null); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const applyPreset = (users, months) => setForm(f => ({ ...f, max_users: users, duration_months: months }));

  const validate = () => {
    const e = {};
    if (!form.max_users || form.max_users < 1)             e.max_users       = 'Enter number of users (min 1)';
    if (!form.duration_months || form.duration_months < 1) e.duration_months = 'Enter duration in months (min 1)';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiClient[editMode ? 'put' : 'post']('/Subscriptions.php', form);
      if (res.data.success) {
        alert(res.data.message || (editMode ? 'Updated!' : 'Subscription created!'));
        setShowModal(false); resetForm(); fetchAll();
      } else alert('Error: ' + res.data.message);
    } catch (e) { alert(e.response?.data?.message || 'Error saving.'); }
    finally { setSaving(false); }
  };

  const handleEdit = s => {
    setForm({ subscriptionid: s.subscriptionid, max_users: s.max_users, duration_months: s.duration_months, amount_paid: s.amount_paid, currency: s.currency, payment_reference: s.payment_reference || '', payment_mode: s.payment_mode || 'Cash', notes: s.notes || '', isactive: s.isactive });
    setEditMode(true); setShowModal(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Deactivate this subscription?')) return;
    try {
      const res = await apiClient.delete('/Subscriptions.php', { data: { subscriptionid: id } });
      if (res.data.success) fetchAll();
      else alert('Error: ' + res.data.message);
    } catch { alert('Error deactivating.'); }
  };

  const resetForm  = () => { setForm(emptyForm); setEditMode(false); setErrors({}); };
  const closeModal = () => { setShowModal(false); resetForm(); };

  const limitColor = () => {
    if (!summary?.has_subscription) return '#dc2626';
    const pct = summary.active_count / summary.max_users;
    return pct >= 1 ? '#dc2626' : pct >= 0.8 ? '#f59e0b' : '#16a34a';
  };

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = `w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]`;
  const labelCls  = 'block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const lc = limitColor();

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <CreditCard size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Subscription Management</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Purchase user slots · Each user's expiry is set automatically at creation time</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll}
            className="w-9 h-9 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 text-white transition-all duration-150 hover:bg-white/30"
            style={{ color: '#fff' }}>
            <RefreshCw size={17} />
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
            style={{ color: NAVY }}>
            <Plus size={17} /> New Subscription
          </button>
        </div>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-5">

        {/* ── SUMMARY CARD ── */}
        {summary && (
          <div className="flex items-center justify-between flex-wrap gap-4 bg-white px-5 py-4 rounded-xl shadow-sm"
            style={{ border: `2px solid ${lc}33` }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: lc + '18' }}>
                <Package size={24} style={{ color: lc }} />
              </div>
              <div>
                <div className="font-bold text-[15px] text-gray-800">
                  {summary.has_subscription
                    ? `Active Plan: ${summary.max_users} Users · ${summary.duration_months} Month${summary.duration_months > 1 ? 's' : ''}`
                    : 'No Active Subscription'}
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  {summary.has_subscription
                    ? `${summary.active_count} of ${summary.max_users} slots used · New users get ${summary.duration_months} month${summary.duration_months > 1 ? 's' : ''} from their creation date`
                    : 'Purchase a subscription to add users'}
                </div>
              </div>
            </div>
            {summary.has_subscription && (
              <div className="flex gap-6">
                {[
                  { label: 'Max Users',    val: summary.max_users },
                  { label: 'Active Users', val: summary.active_count },
                  { label: 'Slots Left',   val: Math.max(0, summary.max_users - summary.active_count) },
                  { label: 'Duration',     val: summary.duration_months + 'mo' },
                ].map(({ label, val }) => (
                  <div key={label} className="text-center">
                    <div className="text-[22px] font-extrabold text-gray-800">{val}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-widest">{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TABLE ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading subscriptions…</div>
        ) : subs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <CreditCard size={44} className="text-gray-300 mb-3" />
            <p className="text-xl text-gray-400 font-semibold mb-4">No subscriptions yet.</p>
            <button onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
              <Plus size={16} /> Buy First Subscription
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Users', 'Duration', 'User Expiry Rule', 'Users Linked', 'Slots Left', 'Amount', 'Payment', 'Created', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.subscriptionid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-[12px] font-bold">
                        <Users size={11} /> {s.max_users}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">
                      {s.duration_months} month{s.duration_months > 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-3.5 text-[12px] text-gray-500">
                      Creation date + {s.duration_months} month{s.duration_months > 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-3.5 text-center font-bold text-gray-800">{s.users_linked ?? '—'}</td>
                    <td className="px-4 py-3.5 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[12px] font-bold ${
                        s.slots_left === 0
                          ? 'bg-red-50 text-red-600'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}>
                        {s.slots_left ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-gray-700">
                      {s.currency} {parseFloat(s.amount_paid).toFixed(2)}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="text-[12px] text-gray-700">{s.payment_mode}</div>
                      {s.payment_reference && <div className="text-[11px] text-gray-400">{s.payment_reference}</div>}
                    </td>
                    <td className="px-4 py-3.5 text-[12px] text-gray-500">{fmtDate(s.createdat)}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        s.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {s.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(s)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Edit2 size={13} />
                        </button>
                        {s.isactive && (
                          <button onClick={() => handleDelete(s.subscriptionid)} title="Deactivate"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[580px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <CreditCard size={18} /> {editMode ? 'Edit Subscription' : 'New Subscription'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* Info callout */}
              <div className="flex items-start gap-2.5 px-4 py-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg text-[12px] text-blue-700">
                <CheckCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>User expiry is set <strong>automatically when each user is created</strong>: their account creation date + the duration months below.</span>
              </div>

              {/* Quick presets */}
              {!editMode && (
                <div className="mb-5">
                  <label className={labelCls}>Quick Select</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map(([u, m]) => {
                      const active = form.max_users === u && form.duration_months === m;
                      return (
                        <button key={`${u}-${m}`} type="button" onClick={() => applyPreset(u, m)}
                          className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold cursor-pointer border-2 transition-all duration-150 ${
                            active
                              ? 'text-white border-transparent'
                              : 'text-gray-700 border-gray-200 bg-white hover:border-[#0d1c6b] hover:text-[#0d1c6b]'
                          }`}
                          style={active ? { background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` } : {}}>
                          {u}u · {m}m
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Users + Duration */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Number of Users <span className="text-red-500">*</span></label>
                  <input type="number" min="1" max="1000" value={form.max_users}
                    onChange={e => setForm({ ...form, max_users: e.target.value })}
                    placeholder="e.g. 10" className={inputCls(errors.max_users)} />
                  {errMsg(errors.max_users)}
                  <p className="text-[11px] text-gray-400 mt-1">Max active user slots</p>
                </div>
                <div>
                  <label className={labelCls}>Duration (Months) <span className="text-red-500">*</span></label>
                  <input type="number" min="1" max="120" value={form.duration_months}
                    onChange={e => setForm({ ...form, duration_months: e.target.value })}
                    placeholder="e.g. 6" className={inputCls(errors.duration_months)} />
                  {errMsg(errors.duration_months)}
                  <p className="text-[11px] text-gray-400 mt-1">Each user expires this many months after account creation</p>
                </div>
              </div>

              {/* Preview pill */}
              {form.max_users && form.duration_months && (
                <div className="flex items-center gap-2 px-4 py-2.5 mb-4 bg-violet-50 border border-violet-200 rounded-lg text-[13px] text-violet-700 font-medium">
                  <Package size={14} className="flex-shrink-0" />
                  <span><strong>{form.max_users} users</strong>, each active for <strong>{form.duration_months} month{form.duration_months > 1 ? 's' : ''}</strong> from their creation date</span>
                </div>
              )}

              {/* Amount + Currency */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Amount Paid</label>
                  <input type="number" min="0" step="0.01" value={form.amount_paid}
                    onChange={e => setForm({ ...form, amount_paid: e.target.value })}
                    placeholder="0.00" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Currency</label>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={selectCls}>
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Payment Mode + Reference */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })} className={selectCls}>
                    {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Payment Reference</label>
                  <input type="text" value={form.payment_reference}
                    onChange={e => setForm({ ...form, payment_reference: e.target.value })}
                    placeholder="Txn ID / Cheque no." maxLength="100" className={inputCls(false)} />
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className={labelCls}>Notes</label>
                <textarea rows={2} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any additional notes…"
                  className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] resize-y font-[inherit]" />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={saving}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {saving ? 'Saving…' : editMode ? 'Update' : 'Create Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Subscriptions;